/**
 * Debt calculator entity models
 * Constitutional requirement: Regular JavaScript with console logging
 */


/**
 * Credit card debt calculation input model
 */
export class DebtCalculationInput {
  constructor(balance = 0, interestRate = 0, monthlyPayment = 0, calculationMode = 'payoffTime') {
    console.log('🏗️ Creating DebtCalculationInput instance:', { balance, interestRate, monthlyPayment, calculationMode })
    
    this.balance = parseFloat(balance) || 0
    this.interestRate = parseFloat(interestRate) || 0
    this.monthlyPayment = parseFloat(monthlyPayment) || 0
    this.calculationMode = calculationMode
    this.createdAt = new Date().toISOString()
    
    console.log('✅ DebtCalculationInput created:', this)
  }

  /**
   * Convert to plain object for storage/serialization
   * @returns {Object} Plain object representation
   */
  toJSON() {
    console.log('📦 Converting DebtCalculationInput to JSON')
    return {
      balance: this.balance,
      interestRate: this.interestRate,
      monthlyPayment: this.monthlyPayment,
      calculationMode: this.calculationMode,
      createdAt: this.createdAt
    }
  }

  /**
   * Create instance from plain object
   * @param {Object} data - Plain object data
   * @returns {DebtCalculationInput} New instance
   */
  static fromJSON(data) {
    console.log('📤 Creating DebtCalculationInput from JSON:', data)
    const instance = new DebtCalculationInput(
      data.balance,
      data.interestRate,
      data.monthlyPayment,
      data.calculationMode
    )
    instance.createdAt = data.createdAt || instance.createdAt
    return instance
  }

  /**
   * Validate the input data
   * @returns {Object} Validation result with isValid and errors
   */
  validate() {
    console.log('🔍 Validating DebtCalculationInput:', this)
    
    const errors = []
    
    if (this.balance <= 0 || this.balance > 1000000) {
      errors.push('Balance must be between $1 and $1,000,000')
    }
    
    if (this.interestRate < 0 || this.interestRate > 100) {
      errors.push('Interest rate must be between 0% and 100%')
    }
    
    if (this.monthlyPayment <= 0 || this.monthlyPayment > 100000) {
      errors.push('Monthly payment must be between $1 and $100,000')
    }
    
    console.log('Calculation mode:', this.calculationMode)
    if (!['monthlyPayment', 'payoffTime'].includes(this.calculationMode)) {
      errors.push('Calculation mode must be either "monthlyPayment" or "payoffTime"')
    }
    
    const result = {
      isValid: errors.length === 0,
      errors
    }
    
    console.log('🔍 Validation result:', result)
    return result
  }
}

/**
 * Monthly payment schedule entry model
 */
export class PaymentScheduleEntry {
  constructor(month, startingBalance, payment, interestCharge, principalPayment, endingBalance) {
    console.log('🏗️ Creating PaymentScheduleEntry for month:', month)
    
    this.month = parseInt(month)
    this.startingBalance = parseFloat(startingBalance) || 0
    this.payment = parseFloat(payment) || 0
    this.interestCharge = parseFloat(interestCharge) || 0
    this.principalPayment = parseFloat(principalPayment) || 0
    this.endingBalance = parseFloat(endingBalance) || 0
    
    console.log('✅ PaymentScheduleEntry created:', this)
  }

  /**
   * Convert to plain object for storage/serialization
   * @returns {Object} Plain object representation
   */
  toJSON() {
    return {
      month: this.month,
      startingBalance: this.startingBalance,
      payment: this.payment,
      interestCharge: this.interestCharge,
      principalPayment: this.principalPayment,
      endingBalance: this.endingBalance
    }
  }

  /**
   * Create instance from plain object
   * @param {Object} data - Plain object data
   * @returns {PaymentScheduleEntry} New instance
   */
  static fromJSON(data) {
    return new PaymentScheduleEntry(
      data.month,
      data.startingBalance,
      data.payment,
      data.interestCharge,
      data.principalPayment,
      data.endingBalance
    )
  }
}

/**
 * Complete debt calculation result model
 */
export class DebtCalculationResult {
  constructor(input, paymentSchedule = [], summary = {}) {
    console.log('🏗️ Creating DebtCalculationResult with', paymentSchedule.length, 'payments')
    
    this.input = input instanceof DebtCalculationInput ? input : DebtCalculationInput.fromJSON(input)
    this.paymentSchedule = paymentSchedule.map(entry => 
      entry instanceof PaymentScheduleEntry ? entry : PaymentScheduleEntry.fromJSON(entry)
    )
    this.summary = summary
    this.calculatedAt = new Date().toISOString()
    
    console.log('✅ DebtCalculationResult created with summary:', this.summary)
  }

  /**
   * Calculate and populate summary statistics
   */
  calculateSummary() {
    console.log('📊 Calculating summary statistics for debt calculation result')
    
    if (this.paymentSchedule.length === 0) {
      console.log('⚠️ No payment schedule available for summary calculation')
      this.summary = {
        totalMonths: 0,
        totalPayments: 0,
        totalInterest: 0,
        totalPrincipal: 0,
        payoffDate: null
      }
      return
    }

    const totalInterest = this.paymentSchedule.reduce((sum, entry) => sum + entry.interestCharge, 0)
    const totalPayments = this.paymentSchedule.reduce((sum, entry) => sum + entry.payment, 0)
    const totalPrincipal = totalPayments - totalInterest
    const payoffDate = new Date()
    payoffDate.setMonth(payoffDate.getMonth() + this.paymentSchedule.length)

    this.summary = {
      totalMonths: this.paymentSchedule.length,
      totalPayments: parseFloat(totalPayments.toFixed(2)),
      totalInterest: parseFloat(totalInterest.toFixed(2)),
      totalPrincipal: parseFloat(totalPrincipal.toFixed(2)),
      payoffDate: payoffDate.toISOString()
    }

    console.log('📊 Summary calculated:', this.summary)
  }

  /**
   * Convert to plain object for storage/serialization
   * @returns {Object} Plain object representation
   */
  toJSON() {
    console.log('📦 Converting DebtCalculationResult to JSON')
    return {
      input: this.input.toJSON(),
      paymentSchedule: this.paymentSchedule.map(entry => entry.toJSON()),
      summary: this.summary,
      calculatedAt: this.calculatedAt
    }
  }

  /**
   * Create instance from plain object
   * @param {Object} data - Plain object data
   * @returns {DebtCalculationResult} New instance
   */
  static fromJSON(data) {
    console.log('📤 Creating DebtCalculationResult from JSON')
    const result = new DebtCalculationResult(
      data.input,
      data.paymentSchedule,
      data.summary
    )
    result.calculatedAt = data.calculatedAt || result.calculatedAt
    return result
  }

  /**
   * Get chart data for balance over time
   * @returns {Object} Chart.js compatible data object
   */
  getBalanceChartData() {
    console.log('📈 Generating balance chart data')
    
    if (this.paymentSchedule.length === 0) {
      console.log('⚠️ No payment schedule available for chart data')
      return { labels: [], datasets: [] }
    }

    const labels = this.paymentSchedule.map(entry => `Month ${entry.month}`)
    const balanceData = this.paymentSchedule.map(entry => entry.endingBalance)

    const chartData = {
      labels,
      datasets: [{
        label: 'Remaining Balance',
        data: balanceData,
        borderColor: 'rgb(255, 159, 64)', // Orange color per constitutional requirement
        backgroundColor: 'rgba(255, 159, 64, 0.1)',
        tension: 0.1
      }]
    }

    console.log('📈 Balance chart data generated with', labels.length, 'data points')
    return chartData
  }

  /**
   * Get chart data for payment breakdown
   * @returns {Object} Chart.js compatible data object
   */
  getPaymentBreakdownChartData() {
    console.log('📊 Generating payment breakdown chart data')
    
    if (this.paymentSchedule.length === 0) {
      console.log('⚠️ No payment schedule available for payment breakdown chart')
      return { labels: [], datasets: [] }
    }

    const labels = this.paymentSchedule.map(entry => `Month ${entry.month}`)
    const interestData = this.paymentSchedule.map(entry => entry.interestCharge)
    const principalData = this.paymentSchedule.map(entry => entry.principalPayment)

    const chartData = {
      labels,
      datasets: [
        {
          label: 'Interest Payment',
          data: interestData,
          backgroundColor: 'rgba(128, 128, 128, 0.7)', // Grey color per constitutional requirement
          borderColor: 'rgb(128, 128, 128)'
        },
        {
          label: 'Principal Payment',
          data: principalData,
          backgroundColor: 'rgba(255, 159, 64, 0.7)', // Orange color per constitutional requirement
          borderColor: 'rgb(255, 159, 64)'
        }
      ]
    }

    console.log('📊 Payment breakdown chart data generated')
    return chartData
  }
}

